import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Sparkles, RefreshCw, ArrowDown, ArrowUp, Minus, Check, X, FileText } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

type Prediction = {
  id: string;
  source_id: string | null;
  organisation: string | null;
  fred_series_id: string;
  predicted_value: number;
  predicted_value_lower: number | null;
  predicted_value_upper: number | null;
  direction: string | null;
  predicted_by_date: string;
  prediction_made_at: string | null;
  verbatim_quote: string | null;
  baseline_value: number | null;
  evaluated: boolean | null;
  actual_value: number | null;
  actual_date: string | null;
  error_pct: number | null;
  abs_error: number | null;
  was_directionally_correct: boolean | null;
  created_at: string;
};

const TABS = [
  { value: "pending", label: "Pending" },
  { value: "scored", label: "Scored" },
  { value: "by_forecaster", label: "By forecaster" },
  { value: "by_series", label: "By series" },
];

function DirIcon({ d }: { d: string | null }) {
  if (d === "up") return <ArrowUp className="h-3 w-3 text-emerald-600 inline" />;
  if (d === "down") return <ArrowDown className="h-3 w-3 text-destructive inline" />;
  if (d === "flat") return <Minus className="h-3 w-3 text-muted-foreground inline" />;
  return null;
}

export default function ResearchPredictions() {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const qc = useQueryClient();

  const { data: predictions, isLoading } = useQuery({
    queryKey: ["research_macro_predictions"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_macro_predictions")
        .select("*")
        .order("predicted_by_date", { ascending: true })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as Prediction[];
    },
    refetchInterval: 30_000,
  });

  const score = useMutation({
    mutationFn: async (id?: string) => {
      const { data, error } = await supabase.functions.invoke("research-score-predictions", {
        body: id ? { id } : {},
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`Scored ${data?.scored ?? 0} (${data?.skipped ?? 0} awaiting FRED)`);
      qc.invalidateQueries({ queryKey: ["research_macro_predictions"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Score failed"),
  });

  const filtered = useMemo(() => {
    const all = predictions ?? [];
    let list = all;
    if (tab === "pending") list = all.filter((p) => !p.evaluated);
    else if (tab === "scored") list = all.filter((p) => p.evaluated).sort((a, b) => (a.error_pct ?? 9999) - (b.error_pct ?? 9999));
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((p) =>
        (p.organisation ?? "").toLowerCase().includes(n) ||
        p.fred_series_id.toLowerCase().includes(n) ||
        (p.verbatim_quote ?? "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [predictions, tab, q]);

  const byForecaster = useMemo(() => {
    const map = new Map<string, { n: number; scored: number; hits: number; meanErr: number; sumErr: number; errCount: number }>();
    (predictions ?? []).forEach((p) => {
      const org = p.organisation ?? "(unknown)";
      const row = map.get(org) ?? { n: 0, scored: 0, hits: 0, meanErr: 0, sumErr: 0, errCount: 0 };
      row.n++;
      if (p.evaluated) {
        row.scored++;
        if (p.was_directionally_correct) row.hits++;
        if (p.error_pct != null) { row.sumErr += p.error_pct; row.errCount++; }
      }
      map.set(org, row);
    });
    return Array.from(map.entries())
      .map(([org, r]) => ({
        organisation: org,
        n: r.n, scored: r.scored,
        hitRate: r.scored ? r.hits / r.scored : null,
        meanErr: r.errCount ? r.sumErr / r.errCount : null,
      }))
      .sort((a, b) => b.scored - a.scored);
  }, [predictions]);

  const bySeries = useMemo(() => {
    const map = new Map<string, { n: number; scored: number; hits: number; sumErr: number; errCount: number }>();
    (predictions ?? []).forEach((p) => {
      const row = map.get(p.fred_series_id) ?? { n: 0, scored: 0, hits: 0, sumErr: 0, errCount: 0 };
      row.n++;
      if (p.evaluated) {
        row.scored++;
        if (p.was_directionally_correct) row.hits++;
        if (p.error_pct != null) { row.sumErr += p.error_pct; row.errCount++; }
      }
      map.set(p.fred_series_id, row);
    });
    return Array.from(map.entries())
      .map(([sid, r]) => ({
        series_id: sid,
        n: r.n, scored: r.scored,
        hitRate: r.scored ? r.hits / r.scored : null,
        meanErr: r.errCount ? r.sumErr / r.errCount : null,
      }))
      .sort((a, b) => b.n - a.n);
  }, [predictions]);

  const counts = useMemo(() => ({
    pending: (predictions ?? []).filter((p) => !p.evaluated).length,
    scored: (predictions ?? []).filter((p) => p.evaluated).length,
    by_forecaster: byForecaster.length,
    by_series: bySeries.length,
  }), [predictions, byForecaster, bySeries]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">Macro predictions</h2>
          <p className="text-sm text-muted-foreground">
            Quantitative forecasts extracted from research notes, scored against realised FRED observations. Use <Sparkles className="h-3 w-3 inline" /> on any source to extract predictions; <RefreshCw className="h-3 w-3 inline" /> to score all rows whose target date has passed.
          </p>
        </div>
        <Button onClick={() => score.mutate(undefined)} disabled={score.isPending}>
          {score.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Score all due
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
                <Badge variant="secondary" className="ml-2 h-4 text-[10px]">{counts[t.value as keyof typeof counts]}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {(tab === "pending" || tab === "scored") && (
          <Input
            placeholder="Filter by org, series, quote…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
          />
        )}
      </div>

      {(tab === "pending" || tab === "scored") && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{filtered.length} prediction{filtered.length === 1 ? "" : "s"}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Made / Due</TableHead>
                  <TableHead className="w-40">Forecaster</TableHead>
                  <TableHead className="w-28">Series</TableHead>
                  <TableHead className="w-32">Predicted</TableHead>
                  {tab === "scored" && <TableHead className="w-32">Actual</TableHead>}
                  {tab === "scored" && <TableHead className="w-20">Error</TableHead>}
                  {tab === "scored" && <TableHead className="w-16">Dir.</TableHead>}
                  <TableHead>Quote</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
                )}
                {!isLoading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">No entries</TableCell></TableRow>
                )}
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {p.prediction_made_at && <div>made {format(new Date(p.prediction_made_at), "dd/MM/yy")}</div>}
                      <div className="font-medium text-foreground">by {format(new Date(p.predicted_by_date), "dd/MM/yyyy")}</div>
                    </TableCell>
                    <TableCell className="text-xs">{p.organisation ?? <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs"><Badge variant="outline" className="text-[10px] font-mono">{p.fred_series_id}</Badge></TableCell>
                    <TableCell className="text-xs">
                      <span className="font-medium">{Number(p.predicted_value).toLocaleString()}</span>{" "}
                      <DirIcon d={p.direction} />
                      {(p.predicted_value_lower != null || p.predicted_value_upper != null) && (
                        <div className="text-[10px] text-muted-foreground">
                          [{p.predicted_value_lower ?? "?"} – {p.predicted_value_upper ?? "?"}]
                        </div>
                      )}
                    </TableCell>
                    {tab === "scored" && (
                      <TableCell className="text-xs">
                        {p.actual_value != null ? Number(p.actual_value).toLocaleString() : "—"}
                        {p.actual_date && <div className="text-[10px] text-muted-foreground">{format(new Date(p.actual_date), "dd/MM/yy")}</div>}
                      </TableCell>
                    )}
                    {tab === "scored" && (
                      <TableCell className="text-xs">
                        {p.error_pct != null ? `${p.error_pct.toFixed(1)}%` : "—"}
                      </TableCell>
                    )}
                    {tab === "scored" && (
                      <TableCell>
                        {p.was_directionally_correct === true && <Check className="h-3 w-3 text-emerald-600" />}
                        {p.was_directionally_correct === false && <X className="h-3 w-3 text-destructive" />}
                      </TableCell>
                    )}
                    <TableCell className="text-xs max-w-md">
                      <div className="line-clamp-2 italic text-muted-foreground">"{p.verbatim_quote}"</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.source_id && (
                        <Button size="sm" variant="ghost" asChild title="Open source's draft item">
                          <Link to={`/partner/research/sources`}><FileText className="h-3 w-3" /></Link>
                        </Button>
                      )}
                      {!p.evaluated && (
                        <Button size="sm" variant="ghost" disabled={score.isPending}
                          onClick={() => score.mutate(p.id)} title="Score this row">
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "by_forecaster" && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Forecaster</TableHead>
                <TableHead className="w-24">Total</TableHead>
                <TableHead className="w-24">Scored</TableHead>
                <TableHead className="w-32">Hit rate (dir.)</TableHead>
                <TableHead className="w-32">Mean abs. error</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {byForecaster.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No entries</TableCell></TableRow>
                )}
                {byForecaster.map((f) => (
                  <TableRow key={f.organisation}>
                    <TableCell className="text-sm font-medium">{f.organisation}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.n}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.scored}</TableCell>
                    <TableCell className="text-xs">
                      {f.hitRate != null ? `${(f.hitRate * 100).toFixed(0)}%` : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {f.meanErr != null ? `${f.meanErr.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "by_series" && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-32">Series</TableHead>
                <TableHead className="w-24">Total</TableHead>
                <TableHead className="w-24">Scored</TableHead>
                <TableHead className="w-32">Hit rate (dir.)</TableHead>
                <TableHead className="w-32">Mean abs. error</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {bySeries.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No entries</TableCell></TableRow>
                )}
                {bySeries.map((s) => (
                  <TableRow key={s.series_id}>
                    <TableCell><Badge variant="outline" className="text-[10px] font-mono">{s.series_id}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.n}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.scored}</TableCell>
                    <TableCell className="text-xs">
                      {s.hitRate != null ? `${(s.hitRate * 100).toFixed(0)}%` : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.meanErr != null ? `${s.meanErr.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
